import { protectedResources } from '@/authConfig';
import acquireAccessToken from '@/http/Helpers/acquireAccessToken';
import { applyPlaygroundModeHeader } from '@/http/Helpers/playgroundModeHeader';
import {
  handleApiResponse,
  rememberRequestBody
} from '@/http/OpenAPI/clients/staleClaimRetry';
import * as Sentry from '@sentry/browser';
import createFetchClient, { type Middleware } from 'openapi-fetch';
import createClient from 'openapi-react-query';

import type { paths } from '../../types/onboarding-workflows';

const { logger } = Sentry;

const scopes = protectedResources.onboardingWebApi.scopes;

const fetchClient = createFetchClient<paths>({
  baseUrl: protectedResources.onboardingWebApi.endpoint
});

/**
 * Auth + URL-rewrite middleware for the onboarding-workflows client. Exported so it can be
 * unit-tested with an injected fetch (see staleClaimRetry.test.ts) without a
 * live MSAL/network round-trip.
 */
export const onboardingWorkflowAuthMiddleware: Middleware = {
  onRequest: async (req) => {
    try {
      const token = await acquireAccessToken(scopes);
      const newUrl = new URL(req.request.url);
      newUrl.pathname = newUrl.pathname.replace(/\/api\//, '');

      const method = req.request.method;
      const headers = new Headers(req.request.headers);
      headers.set('Authorization', `Bearer ${token}`);

      // `new Request(url, existingRequest)` can drop the body in some browsers (empty PUT/POST).
      // Re-materialize the body from a clone so ASP.NET always receives JSON when provided.
      let body: ArrayBuffer | undefined;
      if (method !== 'GET' && method !== 'HEAD') {
        const buf = await req.request.clone().arrayBuffer();
        body = buf.byteLength > 0 ? buf : undefined;
      }

      const authorized = new Request(newUrl.toString(), { method, headers, body });
      applyPlaygroundModeHeader(authorized);
      // Keep the body for a 403 replay: fetch consumes the Request, so onResponse
      // cannot clone it back (ADO #7699).
      await rememberRequestBody(authorized, body);
      return authorized;
    } catch {
      // Still return a request; returning undefined can drop the body and cause ASP.NET "non-empty request body" / command null.
      return req.request;
    }
  },
  onResponse: (ctx) => handleApiResponse(ctx, { api: 'onboarding-workflow', scopes }),
  onError: async (err) => {
    logger.error('Error occurred in onboarding-workflow fetch client', {
      error: err,
      api: 'onboarding-workflow'
    });
  }
};

fetchClient.use(onboardingWorkflowAuthMiddleware);

export const $onboardingWorkflowApi = createClient(fetchClient);
export { fetchClient as onboardingWorkflowFetchClient };
